import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Variant B of the #537 confirmation pattern: the settled indicator for a
 * DEBOUNCED field, where a toast per keystroke would be noise.
 *
 * Lifted verbatim from `BriefingBuilder`, which was the only component in the
 * app with one. The words and the 1500 ms dwell are unchanged — Kasandra has
 * already seen them in Morning Notes and kept them.
 *
 * The colour is the one thing that changed. It was `#16a34a`, a generic green
 * and the app's only off-palette colour; CLAUDE.md's brand section allows none.
 * It is now Periwinkle, which is already the app's primary accent.
 *
 * See `src/lib/saveConfirmation.ts` for variant A (discrete actions) and why a
 * confirmation may only render from a write whose rows were actually read.
 */

/** Periwinkle — brand primary. Was #16a34a before #537. */
export const SAVED_INDICATOR_COLOR = "#5050C8";
export const SAVED_INDICATOR_TEXT = "Saved ✓";
export const SAVED_INDICATOR_MS = 1500;

/**
 * Holds the flash for `SAVED_INDICATOR_MS`, restarting the clock if the field
 * settles again before it elapses. Clears its timer on unmount so a save that
 * lands as she navigates away cannot set state on a gone component.
 *
 * Call `showSaved()` only AFTER the write is known to have succeeded — the
 * whole point of the pattern is that the checkmark is a claim about the
 * database, not about the debounce firing.
 */
export function useSavedFlash(ms: number = SAVED_INDICATOR_MS) {
  const [saved, setSaved] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clear = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };

  const showSaved = useCallback(() => {
    setSaved(true);
    clear();
    timer.current = setTimeout(() => setSaved(false), ms);
  }, [ms]);

  useEffect(() => clear, []);

  return { saved, showSaved };
}

interface SavedIndicatorProps {
  show: boolean;
  /** Layout is the caller's — this component owns the words and the colour. */
  className?: string;
  fontFamily?: string;
}

export default function SavedIndicator({ show, className = "", fontFamily }: SavedIndicatorProps) {
  if (!show) return null;
  return (
    <p
      className={`font-body text-xs ${className}`}
      style={{ color: SAVED_INDICATOR_COLOR, fontFamily }}
      role="status"
      aria-live="polite"
    >
      {SAVED_INDICATOR_TEXT}
    </p>
  );
}
